import * as React from 'react';

import { useCampaignsList } from '@/api/use-campaigns';
import { useExportsClient } from '@/api/exports-client-context';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';

// =============================================================================
// campaign-recipients-export-section.tsx — Export des destinataires de campagne.
// =============================================================================
// DBU §2.7.3 (V1, ADR D8) : l'administrateur dispose d'un EXPORT CSV DES DESTINATAIRES
// (contacts MASTER + collaborateurs de chaque SIRET inscrit à la campagne) pour un
// broadcast manuel via son propre client email. Ce point d'entrée /admin/exports rend
// l'export JOIGNABLE hors du détail de campagne : un SÉLECTEUR de campagne + un bouton.
//
// EXPORT = γ SYNCHRONE (GATE_0 S10) : TÉLÉCHARGEMENT DIRECT (GET → CSV), PAS de job ni
// de polling. Le CONTENU du CSV (colonnes contacts, K-anonymity, RBAC) est rendu SERVEUR
// (CampaignRecipientsExportController, AdminSessionGuard) : le front N'A PAS à connaître
// les colonnes, il ne fait que déclencher le téléchargement pour la campagne choisie.
//
// État de sélection = state React in-memory (RULE_REF_006 : aucun localStorage pour une
// donnée métier). WCAG 2.1 AA : <label htmlFor> lié, bouton aria-label explicite, état
// vide (aucune campagne) annoncé via role="status".

const SELECT_ID = 'campaign-recipients-export-select';
const DESC_ID = 'campaign-recipients-export-description';

export function CampaignRecipientsExportSection(): React.JSX.Element {
  const exportsClient = useExportsClient();
  const campaignsQuery = useCampaignsList();
  const [selectedCampaignId, setSelectedCampaignId] = React.useState('');

  const campaigns = campaignsQuery.data ?? [];
  const hasCampaigns = campaigns.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Export des destinataires de campagne
        </CardTitle>
        <CardDescription id={DESC_ID}>
          Téléchargez la liste des destinataires (contacts MASTER et
          collaborateurs des fournisseurs inscrits) d&apos;une campagne au format
          CSV, pour un envoi manuel via votre client email.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {campaignsQuery.isLoading ? (
          <p role="status" className="text-sm text-muted-foreground">
            Chargement des campagnes…
          </p>
        ) : campaignsQuery.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Impossible de charger la liste des campagnes.
          </p>
        ) : !hasCampaigns ? (
          <p role="status" className="text-sm text-muted-foreground">
            Aucune campagne disponible pour l&apos;export des destinataires.
          </p>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor={SELECT_ID}>Campagne</Label>
              <select
                id={SELECT_ID}
                aria-describedby={DESC_ID}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={selectedCampaignId}
                onChange={(e) => setSelectedCampaignId(e.target.value)}
              >
                <option value="">Sélectionnez une campagne…</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.campaignId} value={campaign.campaignId}>
                    {campaign.intitule}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              disabled={selectedCampaignId === ''}
              onClick={() =>
                exportsClient.downloadCampaignRecipients(selectedCampaignId)
              }
              aria-label="Exporter les destinataires de la campagne sélectionnée au format CSV"
            >
              Exporter les destinataires (CSV)
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
